import { useEffect, useState } from "react";
import { Loader2, Inbox, RefreshCw, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS = { nuova: "bg-emerald-100 text-emerald-800", contattata: "bg-amber-100 text-amber-800", chiusa: "bg-slate-200 text-slate-700" };
const LABEL = { nuova: "Nuova", contattata: "Contattata", chiusa: "Chiusa" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

function Row({ r, onSaved }) {
  const [notes, setNotes] = useState(r.notes || "");
  const [busy, setBusy] = useState(false);
  const save = async (patch) => {
    setBusy(true);
    try { await api.put(`/superadmin/demo-requests/${r.id}`, patch); onSaved(); toast.success("Richiesta aggiornata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    if (!window.confirm(`Reinviare l'avviso interno allo Studio per «${r.company}»?\nNessuna email verrà inviata al richiedente; il lead non viene modificato.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/superadmin/demo-requests/${r.id}/resend-notification`); onSaved(); toast[data.email_sent ? "success" : "error"](data.email_sent ? "Avviso reinviato" : `Avviso non recapitato: ${data.email_error || "errore"}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid={`demo-req-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS[r.status]}`} data-testid={`demo-req-status-${r.id}`}>{LABEL[r.status]}</span>
        <span className="font-semibold text-slate-900">{r.company}</span>
        <span className="text-sm text-slate-600">· {r.name}</span>
        <span className="ml-auto text-xs text-slate-500">{fmt(r.created_at)}</span>
      </div>
      {r.email_error && (
        <p className="flex flex-wrap items-center gap-2 text-[11px] text-amber-700" data-testid={`demo-req-mailwarn-${r.id}`}>
          <span>{r.email_sent ? "Avviso email recapitato solo in parte" : "Avviso email non inviato"} ({r.email_error})</span>
          <Button size="sm" variant="outline" disabled={busy} onClick={resend} className="h-6 px-2 text-[11px]" data-testid={`demo-req-resend-${r.id}`}><Mail className="h-3 w-3 mr-1" />Reinvia avviso{r.notify_resend_count ? ` (${r.notify_resend_count})` : ""}</Button>
        </p>
      )}
      {r.email_checked_at && <p className="text-[11px] text-slate-500" data-testid={`demo-req-confirm-${r.id}`}>Conferma al richiedente: {r.confirm_sent ? "inviata" : r.confirm_error || "non inviata"}</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-1 text-xs text-slate-700">
        <span>Email: <a className="text-[#1E4FD8]" href={`mailto:${r.email}`}>{r.email}</a></span>
        <span>Telefono: <a className="text-[#1E4FD8]" href={`tel:${r.phone}`}>{r.phone}</a></span>
        <span>Settore: <b>{r.sector || "—"}</b></span>
        <span>Rubrica: <b>{r.contacts_estimate || "—"}</b>{r.city ? ` · ${r.city}` : ""}</span>
      </div>
      {r.message && <p className="text-sm text-slate-700 whitespace-pre-wrap border-l-2 border-slate-200 pl-3">{r.message}</p>}
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note interne…" className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-sm" data-testid={`demo-req-notes-${r.id}`} />
        <Button size="sm" variant="outline" disabled={busy || notes === (r.notes || "")} onClick={() => save({ notes })} className="h-8" data-testid={`demo-req-save-notes-${r.id}`}><Save className="h-3.5 w-3.5 mr-1" />Salva note</Button>
        {Object.keys(LABEL).filter((s) => s !== r.status).map((s) => (
          <Button key={s} size="sm" variant="outline" disabled={busy} onClick={() => save({ status: s })} className="h-8" data-testid={`demo-req-set-${s}-${r.id}`}>Segna {LABEL[s].toLowerCase()}</Button>
        ))}
      </div>
    </div>
  );
}

export const DemoRequests = () => {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("");
  const load = async () => { try { setD((await api.get("/superadmin/demo-requests", { params: filter ? { status: filter } : {} })).data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="p-6 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const onSaved = () => load();
  return (
    <div className="space-y-3" data-testid="sa-demo-requests">
      <div className="flex flex-wrap items-center gap-2">
        {["", "nuova", "contattata", "chiusa"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-xs font-medium border ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`} data-testid={`demo-filter-${s || "all"}`}>
            {s ? `${LABEL[s]} · ${d.counts[s]}` : `Tutte · ${Object.values(d.counts).reduce((a, b) => a + b, 0)}`}
          </button>
        ))}
        <Button size="sm" variant="outline" onClick={load} className="ml-auto h-7" data-testid="demo-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      {!d.items.length && <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="demo-empty"><Inbox className="mx-auto h-6 w-6 mb-2" />Nessuna richiesta di dimostrazione.</div>}
      {d.items.map((r) => <Row key={r.id} r={r} onSaved={onSaved} />)}
    </div>
  );
};
